/**
 * ProjectDrawer.tsx
 * Drawer pour la gestion des projets
 * Extrait de ModernSidebar.tsx
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useAppStore, PRESET_FORMATS, PRESET_DPI } from '../../../../services/StateService';
import { useAppCoordinatorOptional } from '../../../../hooks/useAppCoordinator';
import { CloudManager, type ProjectData } from '../../../../CloudManager';
import { logger } from '../../../../utils/ProfessionalLogger';
import { useToast } from '../../common/ToastProvider';
import { Button, IconButton } from '../../../../ui';
import { X } from 'lucide-react';

interface ProjectDrawerProps {
  onClose: () => void;
}

export const ProjectDrawer: React.FC<ProjectDrawerProps> = ({ onClose }) => {
  const project = useAppStore(state => state.project);
  const setProjectFormat = useAppStore(state => state.setProjectFormat);
  const setProjectDpi = useAppStore(state => state.setProjectDpi);
  const _setPrintSettings = useAppStore(state => state.setPrintSettings);
  const setSafeZoneVisible = useAppStore(state => state.setSafeZoneVisible);
  const setBleedVisible = useAppStore(state => state.setBleedVisible);
  const updateProjectDimensions = useAppStore(state => state.updateProjectDimensions);
  const { addToast } = useToast();

  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showProjectsList, setShowProjectsList] = useState(false);
  const [cloudManager, setCloudManager] = useState<CloudManager | null>(null);
  const appCoordinator = useAppCoordinatorOptional();

  useEffect(() => {
    if (appCoordinator) {
      const cm = appCoordinator.getCloudManager();
      if (cm) {
        setCloudManager(cm);
        cm.initialize();
      }
    }
  }, [appCoordinator]);

  const loadProjectsList = useCallback(async () => {
    if (!cloudManager) return;
    setLoading(true);
    try {
      const allProjects = await cloudManager.getAllProjects();
      setProjects(allProjects);
      setShowProjectsList(true);
    } catch (error) {
      logger.error('Erreur lors du chargement des projets:', error);
      addToast({
        type: 'error',
        message: 'Erreur lors du chargement des projets depuis le backend',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [cloudManager, addToast]);

  const handleSaveProject = useCallback(async () => {
    if (!appCoordinator) {
      addToast({
        type: 'error',
        message: 'AppCoordinator non disponible',
        duration: 5000,
      });
      return;
    }
    setSaving(true);
    try {
      await appCoordinator.saveProject(true);
      addToast({
        type: 'success',
        message: 'Projet sauvegardé avec succès !',
        duration: 3000,
      });
      if (showProjectsList) {
        await loadProjectsList();
      }
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde:', error);
      addToast({
        type: 'error',
        message: 'Erreur lors de la sauvegarde du projet',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  }, [appCoordinator, showProjectsList, loadProjectsList, addToast]);

  const handleLoadProject = useCallback(
    async (projectId: string) => {
      if (!cloudManager || !appCoordinator) {
        addToast({
          type: 'error',
          message: 'CloudManager ou AppCoordinator non disponible',
          duration: 5000,
        });
        return;
      }
      setLoading(true);
      try {
        const projectData = await cloudManager.loadProject(projectId);
        if (projectData && projectData.data) {
          await appCoordinator.loadProject(projectData.data);
          addToast({
            type: 'success',
            message: `Projet "${projectData.name}" chargé avec succès !`,
            duration: 3000,
          });
          setShowProjectsList(false);
        } else {
          addToast({
            type: 'error',
            message: 'Projet non trouvé',
            duration: 5000,
          });
        }
      } catch (error) {
        logger.error('Erreur lors du chargement du projet:', error);
        addToast({
          type: 'error',
          message: 'Erreur lors du chargement du projet',
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    },
    [cloudManager, appCoordinator, addToast]
  );

  const handleLoadFromFile = useCallback(() => {
    if (!appCoordinator) {
      addToast({
        type: 'error',
        message: 'AppCoordinator non disponible',
        duration: 5000,
      });
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const projectData = JSON.parse(text);
        await appCoordinator.loadProject(projectData);
        addToast({
          type: 'success',
          message: 'Projet chargé depuis le fichier',
          duration: 3000,
        });
      } catch (error) {
        logger.error('Erreur lors du chargement du fichier:', error);
        addToast({
          type: 'error',
          message: 'Erreur lors du chargement du fichier',
          duration: 5000,
        });
      }
    };
    input.click();
  }, [appCoordinator, addToast]);

  const handleFormatChange = useCallback(
    (formatId: string) => {
      const format = PRESET_FORMATS.find(f => f.id === formatId);
      if (format) {
        setProjectFormat(format);
        updateProjectDimensions();
      }
    },
    [setProjectFormat, updateProjectDimensions]
  );

  const handleDPIChange = useCallback(
    (dpi: number) => {
      setProjectDpi(dpi);
      updateProjectDimensions();
    },
    [setProjectDpi, updateProjectDimensions]
  );

  const handleSafeZoneToggle = useCallback(() => {
    setSafeZoneVisible(!project.safeZoneVisible);
  }, [project.safeZoneVisible, setSafeZoneVisible]);

  const handleBleedToggle = useCallback(() => {
    setBleedVisible(!project.bleedVisible);
  }, [project.bleedVisible, setBleedVisible]);

  return (
    <>
      <div className="drawer-header">
        <h3 className="drawer-title">Paramètres du projet</h3>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label="Fermer"
          icon={<X className="w-4 h-4" />}
        />
      </div>
      <div className="drawer-content">
        {/* Format */}
        <div className="control-group">
          <label>Format</label>
          <select
            className="input"
            value={project.format?.id || 'custom'}
            onChange={e => handleFormatChange(e.target.value)}
          >
            {PRESET_FORMATS.map(format => (
              <option key={format.id} value={format.id}>
                {format.name}
              </option>
            ))}
          </select>
        </div>

        {/* DPI */}
        <div className="control-group">
          <label>DPI</label>
          <select
            className="input"
            value={project.dpi}
            onChange={e => handleDPIChange(Number(e.target.value))}
          >
            {Object.entries(PRESET_DPI).map(([key, preset]) => (
              <option key={key} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>

        {/* Safe Zone */}
        <div className="control-group">
          <label>
            <input
              type="checkbox"
              checked={project.safeZoneVisible}
              onChange={handleSafeZoneToggle}
            />
            Afficher la zone de sécurité
          </label>
        </div>

        {/* Bleed */}
        <div className="control-group">
          <label>
            <input type="checkbox" checked={project.bleedVisible} onChange={handleBleedToggle} />
            Afficher le fond perdu
          </label>
        </div>

        {/* Actions */}
        <div className="control-group flex flex-col gap-2">
          <Button
            variant="primary"
            size="md"
            onClick={handleSaveProject}
            disabled={saving}
            isLoading={saving}
            fullWidth
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={loadProjectsList}
            disabled={loading}
            isLoading={loading}
            fullWidth
          >
            {loading ? 'Chargement...' : 'Charger depuis le backend'}
          </Button>
          <Button variant="secondary" size="md" onClick={handleLoadFromFile} fullWidth>
            Charger depuis un fichier
          </Button>
        </div>

        {/* Liste des projets */}
        {showProjectsList && (
          <div style={{ marginTop: 'var(--spacing-md)', maxHeight: '300px', overflowY: 'auto' }}>
            {projects.map(proj => (
              <div
                key={proj.id}
                onClick={() => handleLoadProject(proj.id)}
                style={{
                  padding: 'var(--spacing-sm)',
                  marginBottom: 'var(--spacing-xs)',
                  background: 'var(--color-bg-secondary)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600 }}>{proj.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                  {new Date(proj.updatedAt || 0).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
